<template>
  <div class="ai-writing">
    <APageHead title="AI 작문 테스트" sub="교재 기반 Passage 작성 및 Q&A 생성을 위한 테스트 도구입니다.">
      <template #actions>
        <ABtn v-if="hasData" kind="ghost" icon="refresh" @click="resetForm">초기화</ABtn>
        <ABtn v-if="hasData" kind="yellow" icon="save" :disabled="isSaving" :loading="isSaving" @click="saveData">
          {{ isSaving ? '저장 중' : '저장' }}
        </ABtn>
      </template>
    </APageHead>

    <!-- 교재 정보 -->
    <ACard>
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">settings</i>
        교재 정보 설정
      </h5>
      <div class="p-form-grid p-form-grid--4 ai-writing__cfg-grid">
        <div class="p-field">
          <label class="p-field__label">교재 바코드 <span class="p-field__req">*</span></label>
          <input v-model="config.barcode" class="p-input" placeholder="예: 9781635190151" />
          <p class="p-field__hint">교재의 ISBN 또는 바코드 번호</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">챕터 번호 <span class="p-field__req">*</span></label>
          <input v-model.number="config.chapterNumber" type="number" class="p-input" placeholder="예: 1" min="1" />
          <p class="p-field__hint">작성할 내용의 챕터 번호</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">고유키 <span class="p-field__req">*</span></label>
          <input v-model="config.uniqueKey" class="p-input" placeholder="예: 1757059361768" />
          <p class="p-field__hint">콘텐츠 식별용 고유 키</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">사용자 ID (userIdx)</label>
          <input v-model.number="config.userIdx" type="number" class="p-input" min="1" placeholder="1" />
          <p class="p-field__hint p-field__hint--warn">실제 사용자 ID를 입력해주세요.</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">타겟 언어</label>
          <select v-model="config.targetLanguage" class="p-input">
            <option value="ko">한국어 (Korean)</option>
            <option value="en">영어 (English)</option>
            <option value="ja">일본어 (Japanese)</option>
            <option value="zh">중국어 (Chinese)</option>
            <option value="es">스페인어 (Spanish)</option>
          </select>
          <p class="p-field__hint">학습 대상 언어</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">모국어</label>
          <select v-model="config.nativeLanguage" class="p-input">
            <option value="en">영어 (English)</option>
            <option value="ko">한국어 (Korean)</option>
            <option value="ja">일본어 (Japanese)</option>
            <option value="zh">중국어 (Chinese)</option>
            <option value="es">스페인어 (Spanish)</option>
          </select>
          <p class="p-field__hint">학습자의 모국어</p>
        </div>
      </div>
    </ACard>

    <!-- Passage 작성 -->
    <ACard>
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">article</i>
        Passage 작성
      </h5>
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">
            타겟 언어 Passage ({{ getLanguageName(config.targetLanguage) }})
            <span class="p-field__req">*</span>
          </label>
          <textarea
            v-model="essayScript.targetEssay"
            class="p-input p-input--area"
            rows="10"
            :placeholder="`${getLanguageName(config.targetLanguage)}로 Passage를 작성하세요...`"
          />
          <p class="p-field__hint">글자 수: {{ (essayScript.targetEssay || '').length }}자</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            모국어 Passage ({{ getLanguageName(config.nativeLanguage) }})
            <span class="p-field__req">*</span>
          </label>
          <textarea
            v-model="essayScript.nativeEssay"
            class="p-input p-input--area"
            rows="10"
            :placeholder="`${getLanguageName(config.nativeLanguage)}로 Passage를 번역/작성하세요...`"
          />
          <p class="p-field__hint">글자 수: {{ (essayScript.nativeEssay || '').length }}자</p>
        </div>
      </div>
      <div class="ai-writing__alert ai-writing__alert--info">
        <i class="material-icons-round">info</i>
        <span><strong>작성 가이드:</strong> 교재 내용을 바탕으로 타겟 언어로 Passage를 작성하고, 학습자가 이해할 수 있도록 모국어로 번역 또는 설명을 추가해주세요.</span>
      </div>
    </ACard>

    <!-- Q&A 작성 -->
    <ACard>
      <div class="ai-writing__qa-head">
        <h5 class="ai-writing__sub-h">
          <i class="material-icons-round">quiz</i>
          질문과 답변
        </h5>
        <ABtn kind="yellow" icon="add" size="sm" @click="addQAPair">Q&A 추가</ABtn>
      </div>

      <div class="ai-writing__alert ai-writing__alert--warn">
        <i class="material-icons-round">help_outline</i>
        <span><strong>작성 안내:</strong> Passage 내용을 기반으로 타겟 언어({{ getLanguageName(config.targetLanguage) }})로 질문과 답변을 작성하세요. 빈 값도 허용됩니다.</span>
      </div>

      <AEmpty
        v-if="questionAndAnswer.questions.length === 0"
        title="질문이 없습니다"
        sub="Q&A 추가 버튼을 클릭하여 질문과 답변을 작성하세요"
        size="sm"
      />

      <div v-else class="ai-writing__qa-list">
        <div
          v-for="(_, index) in questionAndAnswer.questions"
          :key="index"
          class="ai-writing__qa-pair"
        >
          <div class="ai-writing__qa-bar">
            <APill kind="yellow">Q&A #{{ index + 1 }}</APill>
            <ABtn kind="danger" size="sm" icon="delete" icon-only @click="removeQAPair(index)" />
          </div>
          <div class="p-form-grid p-form-grid--2">
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">help</i>
                질문 ({{ getLanguageName(config.targetLanguage) }})
              </label>
              <textarea
                v-model="questionAndAnswer.questions[index]"
                class="p-input p-input--area"
                rows="3"
                :placeholder="`질문 ${index + 1} (선택)`"
              />
            </div>
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">check_circle</i>
                답변 ({{ getLanguageName(config.targetLanguage) }})
              </label>
              <textarea
                v-model="questionAndAnswer.answers[index]"
                class="p-input p-input--area"
                rows="3"
                :placeholder="`답변 ${index + 1} (선택)`"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="ai-writing__qa-foot">
        <ABtn kind="ghost" icon="add_circle" @click="addMultipleQAPairs">여러 Q&A 한번에 추가</ABtn>
      </div>
    </ACard>

    <!-- Stage 1 평가 -->
    <ACard v-if="hasData && !stage1Result">
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">rate_review</i>
        Stage 1 평가 (초기 작성 평가)
      </h5>
      <div class="ai-writing__alert ai-writing__alert--info">
        <i class="material-icons-round">info</i>
        <span><strong>Stage 1 평가:</strong> 작성한 Passage와 Q&A를 AI가 평가하고 수정된 버전을 제공합니다.</span>
      </div>

      <div v-if="isEvaluatingStage1" class="ai-writing__eval-progress">
        <h6>
          <i class="material-icons-round ai-writing__rotating">sync</i>
          AI가 Passage를 분석하고 있습니다...
        </h6>
        <p class="ai-writing__eval-note">⏱️ 평가에는 약 30초~1분 정도 소요됩니다</p>
        <div class="ai-writing__progress-bar">
          <div class="ai-writing__progress-fill ai-writing__progress-fill--err">평가 진행 중...</div>
        </div>
      </div>

      <div v-else class="ai-writing__center-action">
        <ABtn kind="danger-fill" icon="play_arrow" @click="evaluateStage1">Stage 1 평가 시작</ABtn>
      </div>
    </ACard>

    <!-- Stage 1 결과 -->
    <ACard v-if="stage1Result">
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">check_circle</i>
        Stage 1 평가 결과
      </h5>
      <div class="ai-writing__alert ai-writing__alert--ok">
        <i class="material-icons-round">check</i>
        <span><strong>평가 완료!</strong> AI가 작성한 수정 버전을 확인하고, Stage 3으로 진행하세요.</span>
      </div>

      <h6 class="ai-writing__h6">AI 수정 Passage</h6>
      <pre class="ai-writing__revised">{{ getAIEssay() }}</pre>

      <h6 class="ai-writing__h6">AI 수정 질문 & 답변</h6>
      <table v-if="getAIQuestions().length > 0" class="ai-writing__qa-table">
        <thead>
          <tr>
            <th width="10%">#</th>
            <th width="45%">질문</th>
            <th width="45%">답변</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(q, idx) in getAIQuestions()" :key="idx">
            <td class="is-center">{{ idx + 1 }}</td>
            <td>{{ q }}</td>
            <td>{{ getAIAnswers()[idx] || '-' }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="ai-writing__alert ai-writing__alert--info">
        <i class="material-icons-round">info</i>
        AI가 수정한 질문과 답변이 없습니다.
      </div>

      <div v-if="stage1Result.overallReview" class="ai-writing__review-box">
        <h6 class="ai-writing__h6">종합 평가</h6>
        <p><strong>요약:</strong> {{ stage1Result.overallReview.summary }}</p>
        <p v-if="stage1Result.overallReview.strengths && stage1Result.overallReview.strengths.length > 0">
          <strong>강점:</strong>
        </p>
        <ul v-if="stage1Result.overallReview.strengths && stage1Result.overallReview.strengths.length > 0">
          <li v-for="(strength, idx) in stage1Result.overallReview.strengths" :key="idx">{{ strength }}</li>
        </ul>
        <p v-if="stage1Result.overallReview.improvements && stage1Result.overallReview.improvements.length > 0">
          <strong>개선점:</strong>
        </p>
        <ul v-if="stage1Result.overallReview.improvements && stage1Result.overallReview.improvements.length > 0">
          <li v-for="(improvement, idx) in stage1Result.overallReview.improvements" :key="idx">{{ improvement }}</li>
        </ul>
      </div>
    </ACard>

    <!-- Stage 3 입력 -->
    <ACard v-if="stage1Result && !stage3Result">
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">edit</i>
        Stage 3 스크립트 작성
      </h5>
      <div class="ai-writing__alert ai-writing__alert--warn">
        <i class="material-icons-round">info</i>
        <span><strong>Stage 3 평가:</strong> AI 수정 내용을 확인하고, 두 개의 스크립트를 작성하여 최종 평가를 받으세요.</span>
      </div>

      <div class="p-field">
        <label class="p-field__label">First Script <span class="p-field__req">*</span></label>
        <textarea
          v-model="stage3Script.firstScript"
          class="p-input p-input--area"
          rows="8"
          placeholder="AI 수정 내용을 보고 느낀 점이나 개선 사항에 대해 작성하세요..."
        />
        <p class="p-field__hint">AI가 수정한 Passage와 Q&A를 비교하여, 어떤 부분이 수정되었는지, 왜 수정되었는지에 대해 작성하세요.</p>
      </div>

      <div class="p-field">
        <label class="p-field__label">Second Script <span class="p-field__req">*</span></label>
        <textarea
          v-model="stage3Script.secondScript"
          class="p-input p-input--area"
          rows="8"
          placeholder="AI의 수정 내용에 대한 전반적인 평가와 학습한 내용을 작성하세요..."
        />
        <p class="p-field__hint">AI의 피드백이 얼마나 도움이 되었는지, 앞으로 어떻게 개선할 것인지에 대해 작성하세요.</p>
      </div>

      <div v-if="isEvaluatingStage3" class="ai-writing__eval-progress">
        <h6>
          <i class="material-icons-round ai-writing__rotating">sync</i>
          AI가 스크립트를 분석하고 있습니다...
        </h6>
        <p class="ai-writing__eval-note">⏱️ 평가에는 약 30초~1분 정도 소요됩니다</p>
        <div class="ai-writing__progress-bar">
          <div class="ai-writing__progress-fill ai-writing__progress-fill--warn">평가 진행 중...</div>
        </div>
      </div>

      <div v-else class="ai-writing__center-action">
        <ABtn
          kind="yellow"
          icon="send"
          :disabled="!stage3Script.firstScript.trim() || !stage3Script.secondScript.trim()"
          @click="evaluateStage3"
        >Stage 3 평가 시작</ABtn>
      </div>
    </ACard>

    <!-- Stage 3 결과 -->
    <ACard v-if="stage3Result">
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">stars</i>
        Stage 3 최종 평가 결과
      </h5>
      <div class="ai-writing__alert ai-writing__alert--info">
        <i class="material-icons-round">celebration</i>
        <span><strong>최종 평가 완료!</strong> 모든 단계의 평가가 완료되었습니다.</span>
      </div>

      <div v-if="stage3Result.evalResults">
        <h6 class="ai-writing__h6">평가 결과</h6>

        <div v-if="stage3Result.evalResults.firstScriptResults" class="ai-writing__review-box">
          <strong>First Script 평가</strong>
          <div class="ai-writing__eval-line">
            <span>명확성 (Clear):</span>
            <APill :kind="stage3Result.evalResults.firstScriptResults.result?.clear ? 'ok' : 'err'">
              {{ stage3Result.evalResults.firstScriptResults.result?.clear ? '✓ Yes' : '✗ No' }}
            </APill>
          </div>
          <div class="ai-writing__eval-line">
            <span>완성도 (Completed):</span>
            <APill :kind="stage3Result.evalResults.firstScriptResults.result?.completed ? 'ok' : 'err'">
              {{ stage3Result.evalResults.firstScriptResults.result?.completed ? '✓ Yes' : '✗ No' }}
            </APill>
          </div>
          <div v-if="stage3Result.evalResults.firstScriptResults.feedback" class="ai-writing__feedback">
            <strong>피드백:</strong>
            <p>{{ stage3Result.evalResults.firstScriptResults.feedback.koFeedback }}</p>
          </div>
        </div>

        <div v-if="stage3Result.evalResults.secondScruptResults" class="ai-writing__review-box">
          <strong>Second Script 평가</strong>
          <div class="ai-writing__eval-line">
            <span>명확성 (Clear):</span>
            <APill :kind="stage3Result.evalResults.secondScruptResults.result?.clear ? 'ok' : 'err'">
              {{ stage3Result.evalResults.secondScruptResults.result?.clear ? '✓ Yes' : '✗ No' }}
            </APill>
          </div>
          <div class="ai-writing__eval-line">
            <span>완성도 (Completed):</span>
            <APill :kind="stage3Result.evalResults.secondScruptResults.result?.completed ? 'ok' : 'err'">
              {{ stage3Result.evalResults.secondScruptResults.result?.completed ? '✓ Yes' : '✗ No' }}
            </APill>
          </div>
          <div v-if="stage3Result.evalResults.secondScruptResults.feedback" class="ai-writing__feedback">
            <strong>피드백:</strong>
            <p>{{ stage3Result.evalResults.secondScruptResults.feedback.koFeedback }}</p>
          </div>
        </div>
      </div>

      <div v-if="stage3Result.evalResults && stage3Result.evalResults.overallReview" class="ai-writing__review-box">
        <h6 class="ai-writing__h6">종합 평가</h6>
        <strong>한국어 평가:</strong>
        <p class="ai-writing__pre-text">{{ stage3Result.evalResults.overallReview.koOverallReview }}</p>
      </div>

      <div class="ai-writing__center-action">
        <ABtn kind="ghost" icon="refresh" @click="resetEvaluation">새로운 평가 시작</ABtn>
      </div>
    </ACard>

    <!-- 데이터 미리보기 -->
    <ACard v-if="hasData && !stage1Result">
      <h5 class="ai-writing__sub-h">
        <i class="material-icons-round">preview</i>
        데이터 미리보기
      </h5>
      <pre class="ai-writing__preview">{{ formattedData }}</pre>
      <div class="ai-writing__preview-actions">
        <ABtn kind="ghost" icon="content_copy" @click="copyToClipboard">클립보드에 복사</ABtn>
        <ABtn kind="ghost" icon="download" @click="downloadJSON">JSON 다운로드</ABtn>
      </div>
    </ACard>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "AIWritingTest",
  components: { APageHead, ACard, ABtn, APill, AEmpty },
  data() {
    return {
      config: {
        barcode: '',
        chapterNumber: 1,
        uniqueKey: '',
        userIdx: 1,
        targetLanguage: 'ko',
        nativeLanguage: 'en',
      },
      essayScript: { targetEssay: '', nativeEssay: '' },
      questionAndAnswer: { questions: [], answers: [] },
      isSaving: false,
      isEvaluatingStage1: false,
      isEvaluatingStage3: false,
      stage1Result: null,
      stage3Script: { firstScript: '', secondScript: '' },
      stage3Result: null,
      languageMap: {
        ko: '한국어',
        en: '영어',
        ja: '일본어',
        zh: '중국어',
        es: '스페인어',
      },
    };
  },
  computed: {
    hasData() {
      return this.essayScript.targetEssay ||
             this.essayScript.nativeEssay ||
             this.questionAndAnswer.questions.length > 0;
    },
    finalData() {
      const essayKeyMap = {
        ko: 'koEssay', en: 'enEssay', ja: 'jaEssay', zh: 'zhEssay', es: 'esEssay',
      };
      return {
        config: {
          barcode: this.config.barcode,
          chapterNumber: this.config.chapterNumber,
          uniqueKey: this.config.uniqueKey,
          targetLanguage: this.config.targetLanguage,
          nativeLanguage: this.config.nativeLanguage,
          userIdx: this.config.userIdx,
        },
        essayScript: {
          [essayKeyMap[this.config.targetLanguage]]: this.essayScript.targetEssay,
          [essayKeyMap[this.config.nativeLanguage]]: this.essayScript.nativeEssay,
        },
        questionAndAnswer: {
          questions: this.questionAndAnswer.questions,
          answers: this.questionAndAnswer.answers,
        },
      };
    },
    formattedData() {
      return JSON.stringify(this.finalData, null, 2);
    },
  },
  mounted() {
    this.loadSampleData();
  },
  methods: {
    getLanguageName(code) { return this.languageMap[code] || code; },

    addQAPair() {
      this.questionAndAnswer.questions.push('');
      this.questionAndAnswer.answers.push('');
    },

    addMultipleQAPairs() {
      const count = prompt('몇 개의 Q&A를 추가하시겠습니까?', '5');
      if (count && !isNaN(count) && count > 0) {
        for (let i = 0; i < parseInt(count); i++) this.addQAPair();
      }
    },

    removeQAPair(index) {
      if (confirm(`Q&A #${index + 1}을 삭제하시겠습니까?`)) {
        this.questionAndAnswer.questions.splice(index, 1);
        this.questionAndAnswer.answers.splice(index, 1);
      }
    },

    resetForm() {
      if (confirm('모든 입력 내용을 초기화하시겠습니까?')) {
        this.config = {
          barcode: '', chapterNumber: 1, uniqueKey: '', userIdx: 1,
          targetLanguage: 'ko', nativeLanguage: 'en',
        };
        this.essayScript = { targetEssay: '', nativeEssay: '' };
        this.questionAndAnswer = { questions: [], answers: [] };
      }
    },

    async saveData() {
      if (!this.config.barcode || !this.config.uniqueKey) {
        alert('교재 바코드와 고유키는 필수 입력 항목입니다.');
        return;
      }
      if (!this.essayScript.targetEssay || !this.essayScript.nativeEssay) {
        alert('타겟 언어와 모국어 Passage는 필수 입력 항목입니다.');
        return;
      }
      this.isSaving = true;
      try {
        console.log('저장할 데이터:', this.finalData);
        alert('데이터가 콘솔에 출력되었습니다. (API 연동 전)');
      } catch (error) {
        console.error('저장 오류:', error);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        this.isSaving = false;
      }
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.formattedData).then(() => {
        alert('클립보드에 복사되었습니다!');
      }).catch((err) => {
        console.error('복사 실패:', err);
        alert('복사에 실패했습니다.');
      });
    },

    downloadJSON() {
      const blob = new Blob([this.formattedData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `writing_test_${this.config.barcode}_ch${this.config.chapterNumber}_${Date.now()}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    loadSampleData() {
      this.config.barcode = '9781635190151';
      this.config.uniqueKey = '';
      this.essayScript.targetEssay = '이 친구는 제시카이에요. 한국영화클럽 멤버이에요. 미국 사람이에요. 한국 대학교에 다녀요. 일학년이에요.';
      this.essayScript.nativeEssay = 'This friend is Jessica. She is a member of the Korean Film Club. She is American. She goes to Korea University. She is a first-year student.';
      this.questionAndAnswer.questions = [
        '제시카는 어느 대학교에 다녀요?',
        '제시카는 한국영화클럽 멤버예요?',
        '',
        '피터는 어느 대학교에 다녀요?',
        '존는 뉴욕 대학교 학생이에요?',
        '존는 몇 학년이에요?',
      ];
      this.questionAndAnswer.answers = [
        '한국 대학교에 다녀요.',
        '네, 한국영화클럽 멤버예요.',
        '',
        '뉴욕 대학교에 다녀요.',
        '',
        '삼 학년이에요.',
      ];
    },

    async evaluateStage1() {
      if (!this.config.barcode || !this.config.uniqueKey) {
        alert('교재 바코드와 고유키는 필수 입력 항목입니다.');
        return;
      }
      if (!this.essayScript.targetEssay || !this.essayScript.nativeEssay) {
        alert('타겟 언어와 모국어 Passage는 필수 입력 항목입니다.');
        return;
      }
      this.isEvaluatingStage1 = true;
      try {
        const params = new URLSearchParams({
          targetLangCode: this.config.targetLanguage,
          firstLanguageCode: this.config.nativeLanguage,
          targetLangEssay: this.essayScript.targetEssay,
          firstLanguageEssay: this.essayScript.nativeEssay,
          questions: JSON.stringify(this.questionAndAnswer.questions),
          answers: JSON.stringify(this.questionAndAnswer.answers),
        });
        const response = await this.$axios({
          method: 'post',
          url: `/ai-prompt/evaluation-essay-stage1/${this.config.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}?${params.toString()}`,
          loadingBar: true,
        });
        if (response.data && response.data.success) {
          const aiRevisedScript = response.data.aiReviedScript || {};
          this.stage1Result = {
            evalResults: response.data.evalResults,
            overallReview: response.data.overallReview,
            aiRevisedScript: {
              essayScript: aiRevisedScript.ai_essay || '',
              questions: aiRevisedScript.ai_question_list || [],
              answers: aiRevisedScript.ai_answer_list || [],
            },
          };
          alert('Stage 1 평가가 완료되었습니다!');
        } else {
          alert('Stage 1 평가 응답 데이터 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('Stage1 평가 오류:', error);
        let errorMessage = 'Stage 1 평가 중 오류가 발생했습니다.';
        if (error.response) {
          if (error.response.status === 400) errorMessage = '입력 데이터가 올바르지 않습니다.';
          else if (error.response.status === 404) errorMessage = '교과서 콘텐츠를 찾을 수 없습니다.';
          else if (error.response.status === 502) errorMessage = 'AI 평가 서비스에 연결할 수 없습니다.';
          else if (error.response.status === 504) errorMessage = '평가 시간이 초과되었습니다. 다시 시도해주세요.';
        }
        alert(errorMessage);
      } finally {
        this.isEvaluatingStage1 = false;
      }
    },

    async evaluateStage3() {
      if (!this.stage3Script.firstScript.trim() || !this.stage3Script.secondScript.trim()) {
        alert('First Script와 Second Script를 모두 작성해주세요.');
        return;
      }
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) {
        alert('Stage 1 평가 결과가 없습니다. 먼저 Stage 1 평가를 완료해주세요.');
        return;
      }
      this.isEvaluatingStage3 = true;
      try {
        const requestData = {
          userScript: {
            essayScript: this.essayScript.targetEssay,
            questions: this.questionAndAnswer.questions,
            answers: this.questionAndAnswer.answers,
            stage3Script: {
              firstScript: this.stage3Script.firstScript,
              secondScript: this.stage3Script.secondScript,
            },
          },
          aiRevisedScript: {
            essayScript: this.stage1Result.aiRevisedScript.essayScript,
            questions: this.stage1Result.aiRevisedScript.questions,
            answers: this.stage1Result.aiRevisedScript.answers,
          },
        };
        const response = await this.$axios({
          method: 'post',
          url: `/ai-prompt/evaluation-essay-stage3/${this.config.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`,
          data: requestData,
          loadingBar: true,
        });
        if (response.data && response.data.success) {
          this.stage3Result = {
            evalResults: response.data.evalResults,
            overallReview: response.data.overallReview,
            aiRevisedScript: response.data.aiReviedScript,
          };
          alert('Stage 3 평가가 완료되었습니다! 모든 평가가 종료되었습니다.');
        } else {
          alert('Stage 3 평가 응답 데이터 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('Stage3 평가 오류:', error);
        let errorMessage = 'Stage 3 평가 중 오류가 발생했습니다.';
        if (error.response) {
          if (error.response.status === 400) errorMessage = '입력 데이터가 올바르지 않습니다. 모든 필드를 확인해주세요.';
          else if (error.response.status === 404) errorMessage = '교과서 콘텐츠를 찾을 수 없습니다.';
          else if (error.response.status === 502) errorMessage = 'AI 평가 서비스에 연결할 수 없습니다.';
          else if (error.response.status === 504) errorMessage = '평가 시간이 초과되었습니다. 다시 시도해주세요.';
        }
        alert(errorMessage);
      } finally {
        this.isEvaluatingStage3 = false;
      }
    },

    resetEvaluation() {
      if (confirm('모든 평가 결과를 초기화하고 새로 시작하시겠습니까?')) {
        this.stage1Result = null;
        this.stage3Result = null;
        this.stage3Script = { firstScript: '', secondScript: '' };
      }
    },

    getAIEssay() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) return 'AI 수정 Passage를 불러올 수 없습니다.';
      return this.stage1Result.aiRevisedScript.essayScript || '수정된 Passage가 없습니다.';
    },

    getAIQuestions() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) return [];
      return this.stage1Result.aiRevisedScript.questions || [];
    },

    getAIAnswers() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) return [];
      return this.stage1Result.aiRevisedScript.answers || [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ai-writing {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__sub-h {
    margin: 0 0 14px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 18px; color: $p-point-deep; }
  }

  &__cfg-grid {
    align-items: end;
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;
    margin-bottom: 14px;

    > i { font-size: 18px; flex-shrink: 0; }

    &--info {
      background: $p-info-soft;
      border-left: 3px solid $p-info;
      color: $p-ink2;
      > i { color: $p-info; }
    }
    &--warn {
      background: $p-warn-soft;
      border-left: 3px solid $p-warn;
      color: $p-warn;
      > i { color: $p-warn; }
    }
    &--ok {
      background: $p-ok-soft;
      border-left: 3px solid $p-ok;
      color: $p-ok;
      > i { color: $p-ok; }
    }
  }

  &__qa-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h5 { margin: 0; }
  }

  &__qa-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 16px;
  }

  &__qa-pair {
    padding: 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel-alt;
  }

  &__qa-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  &__qa-foot {
    display: flex;
    justify-content: center;
    margin-top: 14px;
  }

  &__eval-progress {
    text-align: center;
    padding: 18px 24px;

    h6 {
      margin: 0 0 6px;
      font-size: 13px;
      font-weight: 800;
      color: $p-ink;
    }
  }

  &__eval-note {
    margin: 0 0 14px;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__progress-bar {
    background: $p-panel-alt;
    border-radius: 99px;
    overflow: hidden;
    height: 24px;
    border: 1px solid $p-line;
  }

  &__progress-fill {
    width: 100%;
    height: 100%;
    color: $p-panel;
    font-size: 11px;
    font-weight: 700;
    display: grid;
    place-items: center;
    background-size: 1rem 1rem;
    background-image: linear-gradient(45deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%);
    animation: ai-writing-progress 1s linear infinite;

    &--err  { background-color: $p-err; }
    &--warn { background-color: $p-warn; }
  }

  &__rotating {
    animation: ai-writing-spin 1.5s linear infinite;
    display: inline-block;
  }

  &__center-action {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }

  &__h6 {
    margin: 16px 0 10px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__revised {
    padding: 14px 16px;
    margin: 0 0 14px;
    background: $p-panel-alt;
    border-left: 3px solid $p-point-deep;
    border-radius: $p-radius-md;
    font-size: 13px;
    line-height: 1.6;
    color: $p-ink;
    font-family: $p-font-body;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__qa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-bottom: 14px;

    th, td {
      padding: 8px 12px;
      border: 1px solid $p-line;
      vertical-align: top;
      word-break: break-word;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
      text-align: left;
    }

    .is-center { text-align: center; }
  }

  &__review-box {
    padding: 14px 16px;
    background: $p-panel-alt;
    border-radius: $p-radius-md;
    border: 1px solid $p-line;
    font-size: 12px;
    margin-bottom: 14px;

    p, ul { margin: 6px 0 0; }
    ul { padding-left: 20px; }
  }

  &__eval-line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    font-size: 12px;
  }

  &__feedback {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid $p-line;

    p { margin: 4px 0 0; }
  }

  &__pre-text {
    white-space: pre-wrap;
  }

  &__preview {
    padding: 14px 16px;
    margin: 0 0 12px;
    background: $p-ink;
    color: $p-point;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 11px;
    line-height: 1.55;
    max-height: 320px;
    overflow-y: auto;
    white-space: pre-wrap;
  }

  &__preview-actions {
    display: flex;
    gap: 8px;
  }
}

@keyframes ai-writing-progress {
  0% { background-position: 0 0; }
  100% { background-position: 1rem 0; }
}

@keyframes ai-writing-spin {
  to { transform: rotate(360deg); }
}
</style>
